import type { OrbExtension } from "lib/orb-qr";
import {
  PrismicCollectionDetails,
  PrismicTaskInstructions,
  PrismicWorkTask,
} from "types";
import { prismicClient, prismicFilter } from "./prismic-client";
import { decodeJwt, JWTPayload } from "jose";
import { Storage, StorageKey } from "./storage";
import {
  LabelConfigurationError,
  getSliceCollectionIds,
  resolveTaskInstructionLabels,
  resolveWorkTaskLabels,
} from "./prismic-labels";

// Tags used to select the collection_details document. In local development a
// specific document can be pinned via REACT_APP_PRISMIC_TAG_OVERRIDE so shared
// documents don't need retagging for testing.
function collectionDocumentTags(contributor: string): string[] {
  if (process.env.NODE_ENV === "development") {
    const override = process.env.REACT_APP_PRISMIC_TAG_OVERRIDE;
    if (override) {
      return [override];
    }
    return [contributor, "dev"];
  }
  return [contributor];
}

export async function getPrismicCollectionDocument() {
  const token = Storage.get(StorageKey.TOKEN);

  if (!token) {
    throw new Error("token not found");
  }

  const jwt = decodeJwt(token) as JWTPayload & { contributor: string };
  const contributor = jwt.contributor;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const page = await prismicClient.getByType<PrismicCollectionDetails>(
      "collection_details",
      {
        fetchLinks: ["work_task.work_task_icon"],
        fetchOptions: { signal: controller.signal },
        filters: [
          prismicFilter.at(
            "document.tags",
            collectionDocumentTags(contributor)
          ),
        ],
      }
    );

    return page.results[0] || null;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function getPrismicCollectionDetails() {
  try {
    return (await getPrismicCollectionDocument())?.data || null;
  } catch (err) {
    console.error("Error fetching collection details from Prismic", err);
    return null;
  }
}

export async function getPrismicCollectionTags(): Promise<string[] | null> {
  try {
    return (await getPrismicCollectionDocument())?.tags ?? null;
  } catch (err) {
    console.error("Error fetching collection tags from Prismic", err);
    return null;
  }
}

export async function getPrismicTaskInstructions(
  taskId: string,
  selectedCollection?: PrismicCollectionDetails
) {
  try {
    const token = Storage.get(StorageKey.TOKEN);

    if (!token) {
      throw new Error("token not found");
    }

    const page = await prismicClient.getByType<PrismicTaskInstructions>(
      "task_instructions",
      {
        filters: [prismicFilter.at("my.task_instructions.task_id", taskId)],
        // Two results are enough to reject an ambiguous definition without
        // fetching every matching document. Keep the current locale/ref scope.
        pageSize: 2,
      }
    );

    if (
      page.total_results_size > 1 ||
      page.results.length > 1 ||
      page.next_page
    ) {
      throw new LabelConfigurationError(
        `Task ID ${taskId} is assigned to multiple Prismic task definitions. ` +
          "Please contact the collection manager to resolve the duplicate before capturing."
      );
    }

    const data = page.results[0]?.data;
    if (!data) return null;

    const slices = data.instruction_content || [];
    if (!slices.some((slice) => getSliceCollectionIds(slice).length > 0)) {
      return await resolveTaskInstructionLabels(data);
    }

    let collection: PrismicCollectionDetails | null;
    try {
      collection = selectedCollection ?? (await getPrismicCollectionDocument());
    } catch (error) {
      console.error("Could not load collection for task labels", error);
      throw new LabelConfigurationError(
        "The active collection could not be loaded. Please retry before capturing."
      );
    }
    if (!collection) {
      throw new LabelConfigurationError(
        "The active collection was not found. Please re-enter your collection."
      );
    }
    const collectionId = collection.id;

    // Filter before loading reusable label documents, including inside Work Tasks.
    return await resolveTaskInstructionLabels({
      ...data,
      instruction_content: slices.filter((slice) => {
        const ids = getSliceCollectionIds(slice);
        return ids.length === 0 || ids.includes(collectionId);
      }) as PrismicTaskInstructions["data"]["instruction_content"],
    });
  } catch (err) {
    console.error("Error fetching task instructions from Prismic", err);
    if (err instanceof LabelConfigurationError) throw err;
    return null;
  }
}

export function getWorkTaskDocumentId(field: unknown): string | undefined {
  if (!field || typeof field !== "object") return undefined;

  const relationship = field as {
    id?: unknown;
    type?: unknown;
    link_type?: unknown;
    isBroken?: unknown;
  };
  if (
    relationship.link_type !== "Document" ||
    relationship.type !== "work_task" ||
    relationship.isBroken === true ||
    typeof relationship.id !== "string" ||
    !relationship.id
  ) {
    return undefined;
  }

  return relationship.id;
}

export function getWorkTaskIconUrl(field: unknown): string | undefined {
  if (!getWorkTaskDocumentId(field)) return undefined;
  const url = (field as { data?: { work_task_icon?: { url?: unknown } } }).data
    ?.work_task_icon?.url;
  if (typeof url !== "string" || !url.trim()) return undefined;
  try {
    const parsed = new URL(url);
    return ["https:", "http:"].includes(parsed.protocol) ? url : undefined;
  } catch {
    return undefined;
  }
}

export async function getPrismicWorkTask(documentId: string) {
  try {
    const token = Storage.get(StorageKey.TOKEN);

    if (!token) {
      throw new Error("token not found");
    }

    const document = await prismicClient.getByID<PrismicWorkTask>(documentId);
    return await resolveWorkTaskLabels(document.data);
  } catch (err) {
    console.error(`Error fetching Work Task ${documentId} from Prismic`, err);
    if (err instanceof LabelConfigurationError) throw err;
    return null;
  }
}

// Extract the extension and optional QR parameters from Orb task instructions.
export function extractOrbExtensionFromInstructions(
  instructions: PrismicTaskInstructions["data"] | null
): OrbExtension | null {
  if (!instructions?.instruction_content) {
    return null;
  }

  // Look for instruction_text___image slice that contains the extension field
  for (const slice of instructions.instruction_content) {
    if (
      slice.slice_type === "instruction_text___image" &&
      slice.primary?.extension
    ) {
      const extensionField = slice.primary.extension;
      if (typeof extensionField === "string") {
        const value = extensionField.split("=")[1]?.trim();
        if (!value) return null;

        // Preserve numeric values for existing extensions and saved confirmations.
        if (/^\d+$/.test(value)) {
          const extension = Number(value);
          return Number.isSafeInteger(extension) ? extension : null;
        }

        // ForcedCapture: 7:<positive timeout in seconds>[:<LEDs off: 0 or 1>].
        // Require complete parameters instead of accepting only a numeric prefix.
        const forcedCapture = /^7:([1-9]\d*)(?::[01])?$/.exec(value);
        if (forcedCapture && Number.isSafeInteger(Number(forcedCapture[1]))) {
          return value;
        }
        return null;
      }
    }
  }

  return null;
}

// Helper function to safely strip HTML tags and decode HTML entities
export const stripHtmlTags = (html: string): string => {
  if (!html) return "";

  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get text content which automatically handles HTML entities and removes tags
  return tempDiv.textContent || tempDiv.innerText || "";
};

// Helper function to render Prismic structured text to HTML
export const renderStructuredText = (structuredText: any): string => {
  if (!structuredText) return "";

  if (typeof structuredText === "string") return structuredText;

  if (Array.isArray(structuredText)) {
    return structuredText
      .map((element: any) => {
        if (element.type === "paragraph") {
          const text = renderTextWithSpans(element.text, element.spans);
          return `<p>${text}</p>`;
        }
        if (element.type === "heading1") {
          const text = renderTextWithSpans(element.text, element.spans);
          return `<h1 class="text-2xl font-bold mb-4">${text}</h1>`;
        }
        if (element.type === "heading2") {
          const text = renderTextWithSpans(element.text, element.spans);
          return `<h2 class="text-xl font-bold mb-3">${text}</h2>`;
        }
        if (element.type === "heading3") {
          const text = renderTextWithSpans(element.text, element.spans);
          return `<h3 class="text-lg font-bold mb-2">${text}</h3>`;
        }
        if (element.type === "heading4") {
          const text = renderTextWithSpans(element.text, element.spans);
          return `<h4 class="text-base font-bold mb-2">${text}</h4>`;
        }
        if (element.type === "list-item") {
          const text = renderTextWithSpans(element.text, element.spans);
          return `<li>${text}</li>`;
        }
        // Handle formatted text spans
        return renderTextWithSpans(element.text, element.spans) || "";
      })
      .join("");
  }

  return structuredText.toString();
};

// Helper function to render text with formatting spans
export const renderTextWithSpans = (text: string, spans: any[]): string => {
  if (!text) return "";
  if (!spans || spans.length === 0) return text;

  let result = text;

  // Sort spans by start position in reverse order to avoid offset issues
  const sortedSpans = [...spans].sort((a, b) => b.start - a.start);

  sortedSpans.forEach((span) => {
    const beforeText = result.substring(0, span.start);
    const spanText = result.substring(span.start, span.end);
    const afterText = result.substring(span.end);

    let wrappedText = spanText;

    switch (span.type) {
      case "strong":
        wrappedText = `<strong class="font-bold">${spanText}</strong>`;
        break;
      case "em":
        wrappedText = `<em class="italic">${spanText}</em>`;
        break;
      case "hyperlink":
        wrappedText = `<a href="${
          span.data?.url || "#"
        }" class="text-blue-600 underline" target="_blank" rel="noopener">${spanText}</a>`;
        break;
      default:
        // Keep original text for unknown span types
        break;
    }

    result = beforeText + wrappedText + afterText;
  });

  return result;
};

// Helper function to extract additionalDataRequired from instruction slices
export const getAdditionalDataRequired = (
  instructions: PrismicTaskInstructions["data"] | null
): string | null => {
  if (!instructions?.instruction_content) return null;

  for (const slice of instructions.instruction_content) {
    if (slice.primary?.additionaldatarequired) {
      return String(slice.primary.additionaldatarequired);
    }
  }

  return null;
};

export const getAdditionalDataRequiredFields = (
  instructions: PrismicTaskInstructions["data"] | null
): string[] =>
  parseAdditionalDataRequired(getAdditionalDataRequired(instructions));

export const parseAdditionalDataRequired = (value: unknown): string[] => {
  const fieldValue = String(value || "").trim();
  if (
    !fieldValue ||
    fieldValue === "null" ||
    fieldValue === "undefined" ||
    fieldValue.toLowerCase() === "none"
  ) {
    return [];
  }

  return fieldValue
    .split(",")
    .map((field) => field.trim())
    .filter(Boolean);
};
